import type { Namespace } from './types';
export const zh: Namespace = {
  redeems: {
    title: '兑换记录',
    empty: '没有符合条件的兑换记录',
    states: {
      10: '待处理',
      20: '处理中',
      40: '已兑入',
      60: '失败',
      80: '结果未知',
      85: '重试已排',
      86: '动态失败',
      unknown: '未知状态',
    },
    errors: {
      // Steam 官网原文：每码只取该分支的一句主要句子（陈述事实那句），中英同口径。
      // 别改写成我方措辞——用户要拿它去对 Steam 官网/客服。
      already_redeemed: '您输入的 Steam 钱包充值码已被兑换。',
      already_redeemed_self: '您输入的 Steam 钱包充值码此前已在您的账户中兑换过。',
      invalid_code: '充值码可能无效。',
      error: '兑换所输入的充值码时出现错误。',
      captcha_required: '您必须完成人机验证，才能尝试兑换您的礼品卡。',
      address_required: '兑换钱包充值码前需先填写账单地址',
      restricted: '您的 Steam 账户目前无法激活 Steam 钱包充值码。',
      // 我方系统错误：没有官网原文可摘，用我方措辞。
      // `expired`：后端 TTL 清扫写的码——兑换行在「待处理」停留超 TTL ⇒ 判失败。
      expired: '兑换超时未完成',
      redeem_failed: '重试全部失败',
      // 我方系统错误（2026-10-23 步⑤落终态那两档）：读不到余额 ⇒ 判不出
      // 这张卡兑没兑进去 ⇒ 说「结果未知」，不猜。
      history_unavailable: '取不到 Steam 钱包历史页（会话可能已过期），本次兑换结果未知。',
      balance_unavailable: 'Steam 钱包历史页里取不到余额，本次兑换结果未知。',
      // 软会话档（EResult 21 NotLoggedOn）：码没被评价，重登/刷凭据后可重交；
      // 官网判决树把 21 落 default 分支 ⇒ 无官网原文可摘，用我方措辞。
      not_logged_on: '账号已离线，暂时无法兑换。',
    },
    failedPage: {
      title: 'Steam 兑换失败说明',
      codeLabel: '失败类型',
      supportLabel: '前往 Steam 客服',
      unknown: '未知的兑换失败类型：{{code}}（该错误无对应的 Steam 官网文案）',
    },
    fields: {
      orderNo: '订单号',
      walletCode: '钱包码',
      username: '账号',
      cardAmount: '面额',
      balanceDelta: '余额差',
      balanceBefore: '兑前余额',
      balanceAfter: '兑后余额',
      state: '状态',
      detail: '详情',
      submittedBy: '操作人',
      updatedAt: '更新时间',
      createdAt: '创建时间',
    },
    kpi: {
      states: '状态',
      cardTotal: '已兑面额',
      deltaTotal: '余额差净额',
    },
    actions: {
      export: '导出 Excel',
      exported: '导出已开始下载',
    },
    export: {
      sheetName: '兑换历史',
    },
    modal: {
      exportTitle: '导出兑换历史',
      exportScope: '导出范围',
    },
  },
};

export const en: Namespace = {
  redeems: {
    title: 'Redemption history',
    empty: 'No redemption records match the filters',
    states: {
      10: 'Pending',
      20: 'Processing',
      40: 'Redeemed',
      60: 'Failed',
      80: 'Unknown result',
      85: 'Retry scheduled',
      86: 'Dynamic failure',
      unknown: 'Unknown state',
    },
    errors: {
      // Steam official wording: one main sentence per branch (the statement, not the
      // "what to do" follow-up); zh and en excerpt the same sentence. Don't reword.
      already_redeemed: 'The Steam Wallet code you have entered has already been redeemed.',
      already_redeemed_self:
        'The Steam Wallet code you have entered has already been previously redeemed on your account.',
      invalid_code: 'The code may be invalid.',
      error: 'There was an error redeeming the entered code.',
      captcha_required: 'You must complete the captcha to attempt to redeem your gift card.',
      address_required: 'billing address required before redeeming wallet codes',
      restricted: 'Your Steam account is currently restricted from activating Steam Wallet codes.',
      // Our own system errors: no Steam original to excerpt.
      // `expired` is written by the backend TTL sweep: a redemption row that
      // stayed PENDING past its TTL is marked failed.
      expired: 'Redemption timed out before completing',
      redeem_failed: 'All retry attempts failed',
      // Our own system errors (2026-10-23, the two step-5 terminal states):
      // without a balance we cannot tell whether the card was redeemed ⇒
      // report "unknown", never guess.
      history_unavailable:
        'Could not read the Steam wallet history page (the session may have expired); the result of this redemption is unknown.',
      balance_unavailable:
        'Could not read the balance from the Steam wallet history page; the result of this redemption is unknown.',
      // 软会话档（EResult 21 NotLoggedOn）：码没被评价，重登/刷凭据后可重交；
      // 官网判决树把 21 落 default 分支 ⇒ 无官网原文可摘，英文与后端文案表
      // 同句（steam_messages.json 逐字闸盯着）。
      not_logged_on:
        'The account is offline for redemptions — refresh credentials and resubmit; ' +
        'this code was not evaluated (EResult 21 NotLoggedOn).',
    },
    failedPage: {
      title: 'Steam redemption failure',
      codeLabel: 'Failure type',
      supportLabel: 'Open Steam Support',
      unknown: 'Unknown redemption failure type: {{code}} (no Steam official text for this error)',
    },
    fields: {
      orderNo: 'Order',
      walletCode: 'Wallet code',
      username: 'Account',
      cardAmount: 'Card value',
      balanceDelta: 'Balance delta',
      balanceBefore: 'Balance before',
      balanceAfter: 'Balance after',
      state: 'State',
      detail: 'Detail',
      submittedBy: 'Operator',
      updatedAt: 'Updated at',
      createdAt: 'Created at',
    },
    kpi: {
      cardTotal: 'Card value redeemed',
      deltaTotal: 'Net balance change',
      states: 'State',
    },
    actions: {
      export: 'Export Excel',
      exported: 'Export started — check your downloads',
    },
    export: {
      sheetName: 'Redemption history',
    },
    modal: {
      exportTitle: 'Export redemption history',
      exportScope: 'Export scope',
    },
  },
};
